/**
 * Monthly report — flags, leave, attendance and tasks for one month.
 * Route: /reports
 * @file src/web/features/reports/pages/index.tsx
 *
 * @llm-rule WHEN: Anyone opens the monthly report from the profile menu
 * @llm-rule NOTE: Grouped by rank because that is how the reporting line
 *   reads — a lead sees Employees, a Head also sees Managers, the Company Admin also
 *   sees Heads. Everyone gets their own row under "You".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Skeleton, toast } from '@bloomneo/uikit';
import { CalendarRange, ChevronLeft, ChevronRight, Download, Search, X } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, PersonAvatar, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { FLAG_LABEL, FLAG_SHORT, type FlagCode } from '../../../shared/flags';
import { PersonMonthDialog } from '../components/PersonMonthDialog';

interface Row {
  person: {
    id: string;
    name: string | null;
    email: string;
    orgRole: OrgRole;
    title: string | null;
    initials: string | null;
  };
  isSelf: boolean;
  attendance: { daysPresent: number; onSite: number; offSite: number; signedOff: number };
  leave: { days: number; requests: number; byType: Record<string, number> };
  flags: {
    total: number;
    /** Not yet waived — the number that needs attention. */
    active: number;
    open: number;
    waived: number;
    byCode: Array<{ code: string; count: number }>;
  };
  tasks: { total: number; done: number; overdue: number; closedLate: number };
}

/** Rank order for the section headings, most senior first. */
const GROUPS: Array<{ role: OrgRole; heading: string }> = [
  { role: 'HEAD', heading: 'Heads' },
  { role: 'MANAGER', heading: 'Managers' },
  { role: 'EMPLOYEE', heading: 'Employees' },
];

function Cell({ value, muted }: { value: number | string; muted?: boolean }) {
  return (
    <td className={`px-2 py-2.5 text-center text-[12.5px] ${muted ? 'text-ink-ghost' : 'text-ink'}`}>
      {value}
    </td>
  );
}

function PersonRows({
  rows,
  onOpen,
}: {
  rows: Row[];
  onOpen: (row: Row, tab: 'flags' | 'tasks' | 'offsite') => void;
}) {
  return (
    <>
      {rows.map((row) => (
        <tr key={row.person.id} className="border-t border-rule transition-colors hover:bg-paper-warm">
          <td className="px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <PersonAvatar person={row.person} size={26} />
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-medium text-ink">
                  {row.person.name}
                  {row.isSelf && <span className="ml-1.5 text-[10.5px] text-ink-ghost">(you)</span>}
                </div>
                <div className="truncate text-[10.5px] text-ink-ghost">
                  {row.person.title || ROLE_LABEL[row.person.orgRole]}
                </div>
              </div>
            </div>
          </td>

          <Cell value={row.attendance.daysPresent} muted={row.attendance.daysPresent === 0} />
          {/* Off-site is tracked, never approved — the detail is the record. */}
          <td className="px-2 py-2.5 text-center">
            <button
              type="button"
              onClick={() => onOpen(row, 'offsite')}
              aria-label={`Open ${row.person.name}'s off-site days`}
              className="rounded-lg px-1.5 py-0.5 text-[12.5px] transition-colors hover:bg-paper-warm"
            >
              <span
                className={`tnum underline-offset-2 hover:underline ${
                  row.attendance.offSite > 0 ? 'font-semibold text-offsite' : 'text-ink-ghost'
                }`}
              >
                {row.attendance.offSite}
              </span>
            </button>
          </td>
          <Cell value={row.leave.days || '—'} muted={row.leave.days === 0} />

          {/* Counts are buttons — clicking opens the detail behind them. */}
          <td className="px-2 py-2.5 text-center">
            <button
              type="button"
              onClick={() => onOpen(row, 'flags')}
              aria-label={`Open ${row.person.name}'s flags`}
              className="rounded-lg px-1.5 py-0.5 transition-colors hover:bg-paper-warm"
            >
              <span
                className={`tnum inline-block rounded-full px-2 py-0.5 text-[12px] font-semibold underline-offset-2 hover:underline ${
                  row.flags.active > 0
                    ? 'bg-[var(--state-bad-bg)] text-flag ring-1 ring-inset ring-[var(--state-bad-border)]'
                    : 'text-ink-ghost'
                }`}
              >
                {row.flags.active}
              </span>
              {row.flags.waived > 0 && (
                <span className="mt-0.5 block text-[10px] text-ink-ghost">
                  {row.flags.waived} waived
                </span>
              )}
            </button>
          </td>

          <td className="px-2 py-2.5 text-center">
            <button
              type="button"
              onClick={() => onOpen(row, 'tasks')}
              aria-label={`Open ${row.person.name}'s tasks`}
              className="rounded-lg px-1.5 py-0.5 text-[12.5px] transition-colors hover:bg-paper-warm"
            >
              <span className="text-ink underline-offset-2 hover:underline">{row.tasks.done}</span>
              <span className="text-ink-ghost">/{row.tasks.total}</span>
              {row.tasks.overdue > 0 && (
                <span className="mt-0.5 block text-[10px] font-medium text-offsite">
                  {row.tasks.overdue} overdue
                </span>
              )}
            </button>
          </td>

          <td className="px-3 py-2.5">
            <div className="flex flex-wrap justify-end gap-1">
              {row.flags.byCode.length === 0 ? (
                <span className="text-[10.5px] text-ink-ghost">clean</span>
              ) : (
                row.flags.byCode.map((entry) => (
                  <span
                    key={entry.code}
                    className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10px] text-ink-faint"
                    title={FLAG_LABEL[entry.code as FlagCode] ?? entry.code}
                  >
                    {FLAG_SHORT[entry.code as FlagCode] ?? entry.code} · {entry.count}
                  </span>
                ))
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

/**
 * The same row as a stacked card, for narrow screens.
 *
 * The table needs 720px to stay readable, which on a phone means a sideways
 * scroll and half the numbers off-screen. Below `md` each person becomes a card
 * with the counts as tappable chips, matching the clock-in screen's language.
 */
function PersonCards({
  rows,
  onOpen,
}: {
  rows: Row[];
  onOpen: (row: Row, tab: 'flags' | 'tasks' | 'offsite') => void;
}) {
  return (
    <div className="flex flex-col divide-y divide-rule">
      {rows.map((row) => (
        <div key={row.person.id} className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <PersonAvatar person={row.person} size={30} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-medium text-ink">
                {row.person.name}
                {row.isSelf && <span className="ml-1.5 text-[11px] text-ink-ghost">(you)</span>}
              </div>
              <div className="truncate text-[11.5px] text-ink-ghost">
                {row.person.title || ROLE_LABEL[row.person.orgRole]}
              </div>
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-3 gap-1.5">
            <div className="rounded-[10px] bg-paper-sunk px-2 py-1.5 text-center">
              <div className="text-[15px] font-semibold tabular-nums text-ink">
                {row.attendance.daysPresent}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-ink-ghost">Present</div>
            </div>

            <button
              type="button"
              onClick={() => onOpen(row, 'offsite')}
              className="rounded-[10px] bg-paper-sunk px-2 py-1.5 text-center transition-colors active:bg-paper-warm"
            >
              <div
                className={`text-[15px] font-semibold tabular-nums ${
                  row.attendance.offSite > 0 ? 'text-offsite' : 'text-ink'
                }`}
              >
                {row.attendance.offSite}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-ink-ghost">Off-site</div>
            </button>

            <div className="rounded-[10px] bg-paper-sunk px-2 py-1.5 text-center">
              <div className="text-[15px] font-semibold tabular-nums text-ink">
                {row.leave.days || '—'}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-ink-ghost">Leave</div>
            </div>
          </div>

          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => onOpen(row, 'flags')}
              className={`rounded-[10px] px-2 py-1.5 text-center transition-colors active:opacity-80 ${
                row.flags.active > 0
                  ? 'bg-[var(--state-bad-bg)] ring-1 ring-inset ring-[var(--state-bad-border)]'
                  : 'bg-paper-sunk'
              }`}
            >
              <div
                className={`text-[15px] font-semibold tabular-nums ${
                  row.flags.active > 0 ? 'text-flag' : 'text-ink'
                }`}
              >
                {row.flags.active}
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-ink-ghost">
                Active flags{row.flags.waived > 0 ? ` · ${row.flags.waived} waived` : ''}
              </div>
            </button>

            <button
              type="button"
              onClick={() => onOpen(row, 'tasks')}
              className="rounded-[10px] bg-paper-sunk px-2 py-1.5 text-center transition-colors active:bg-paper-warm"
            >
              <div className="text-[15px] font-semibold tabular-nums text-ink">
                {row.tasks.done}
                <span className="text-ink-ghost">/{row.tasks.total}</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-ink-ghost">
                Tasks{row.tasks.overdue > 0 ? ` · ${row.tasks.overdue} overdue` : ''}
              </div>
            </button>
          </div>

          {row.flags.byCode.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {row.flags.byCode.map((entry) => (
                <span
                  key={entry.code}
                  className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10px] text-ink-faint"
                  title={FLAG_LABEL[entry.code as FlagCode] ?? entry.code}
                >
                  {FLAG_SHORT[entry.code as FlagCode] ?? entry.code} · {entry.count}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReportsPage() {
  const { request } = useAppRequest();

  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  /** Which person's detail is open, and on which tab. */
  const [open, setOpen] = useState<{ row: Row; tab: 'flags' | 'tasks' | 'offsite' } | null>(null);
  const [params, setParams] = useSearchParams();
  /** Set once per ?person= so closing the dialog does not immediately reopen it. */
  const [focusHandled, setFocusHandled] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const result = await request<{ rows: Row[] }>(`/api/report/monthly?year=${year}&month=${month}`);
    if (result.ok) setRows(result.data.rows);
    else toast.error(result.error);
    setLoading(false);
  }, [request, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  function step(delta: number) {
    const next = new Date(Date.UTC(year, month - 1 + delta, 1));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth() + 1);
  }

  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const isCurrentMonth = year === now.getUTCFullYear() && month === now.getUTCMonth() + 1;

  // Arriving from Company today with ?person=<id> means "show me THIS person's
  // month", not the company list — open their dialog as soon as rows land.
  const focusId = params.get('person');
  useEffect(() => {
    if (!focusId || focusHandled || rows.length === 0) return;
    const row = rows.find((r) => r.person.id === focusId);
    setFocusHandled(true);
    if (row) setOpen({ row, tab: 'flags' });
  }, [focusId, focusHandled, rows]);

  // Search matches name, job title, or email — a half-remembered address should
  // find someone just as well as a name.
  const needle = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.person.name, r.person.title, r.person.email]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle)),
    );
  }, [rows, needle]);

  // While searching, your own row is just another result — pinning it to the top
  // would push a match off-screen.
  const self = needle ? undefined : visible.find((r) => r.isSelf);
  const grouped = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        rows: visible.filter((r) => r.person.orgRole === g.role && (!r.isSelf || Boolean(needle))),
      })).filter((g) => g.rows.length > 0),
    [visible, needle],
  );

  /** Plain CSV so the numbers can go into a spreadsheet without retyping. */
  function exportCsv() {
    const header = [
      'Name', 'Role', 'Days present', 'Off-site', 'Leave days',
      'Active flags', 'Waived flags', 'Total flags',
      'Tasks done', 'Tasks total', 'Tasks overdue',
    ];
    const lines = rows.map((r) =>
      [
        r.person.name ?? r.person.email,
        ROLE_LABEL[r.person.orgRole],
        r.attendance.daysPresent,
        r.attendance.offSite,
        r.leave.days,
        r.flags.active ?? r.flags.open,
        r.flags.waived,
        r.flags.total,
        r.tasks.done,
        r.tasks.total,
        r.tasks.overdue,
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(','),
    );

    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly-report-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const Header = (
    <thead>
      <tr className="border-b border-rule bg-paper-sunk/70 text-[10px] uppercase tracking-[0.09em] text-ink-ghost">
        <th className="px-3 py-2.5 text-left font-semibold">Person</th>
        <th className="px-2 py-2.5 text-center font-semibold">Present</th>
        <th className="px-2 py-2.5 text-center font-semibold">Off-site</th>
        <th className="px-2 py-2.5 text-center font-semibold">Leave</th>
        <th className="px-2 py-2.5 text-center font-semibold">Active flags</th>
        <th className="px-2 py-2.5 text-center font-semibold">Tasks</th>
        <th className="px-3 py-2.5 text-right font-semibold">Breakdown</th>
      </tr>
    </thead>
  );

  return (
    <AppShell wide title="Monthly report">
      {/* Month switcher */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" aria-label="Previous month" onClick={() => step(-1)}>
            <ChevronLeft className="size-4" />
          </Button>

          <div className="flex min-w-[190px] items-center justify-center gap-2 rounded-xl border border-rule bg-paper px-4 py-2">
            <CalendarRange className="size-4 text-ink-faint" />
            <span className="text-[14px] font-medium text-ink">{monthLabel}</span>
          </div>

          <Button
            variant="outline"
            size="sm"
            aria-label="Next month"
            disabled={isCurrentMonth}
            onClick={() => step(1)}
          >
            <ChevronRight className="size-4" />
          </Button>

          {!isCurrentMonth && (
            <Button
              variant="ghost"
              size="sm"
              className="text-[12px]"
              onClick={() => {
                setYear(now.getUTCFullYear());
                setMonth(now.getUTCMonth() + 1);
              }}
            >
              This month
            </Button>
          )}
        </div>

        <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="mr-1.5 size-3.5" />
          Export CSV
        </Button>
      </div>

      {/* Search sits below the month picker so it stays reachable on a phone
          without competing with the month controls for the same row. */}
      <div className="relative mt-3">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-ink-ghost"
          strokeWidth={1.9}
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name…"
          aria-label="Search people in this month's report"
          className="h-9 w-full rounded-[10px] border border-line bg-paper pl-9 pr-8 text-[13.5px] text-ink placeholder:text-ink-ghost focus:border-ink-ghost focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-ghost hover:text-ink"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : (
        <div className="flex flex-col gap-5">
          {needle && grouped.length === 0 && (
            <Panel>
              <div className="flex flex-col items-center gap-1.5 py-8 text-center">
                <Search className="size-5 text-ink-faint" strokeWidth={1.6} />
                <p className="text-[13.5px] font-medium text-ink">Nobody matches “{query}”</p>
                <p className="text-[12.5px] text-ink-soft">
                  Search looks at name, title and email.
                </p>
              </div>
            </Panel>
          )}

          {/* Your own month */}
          {self && (
            <Panel padded={false} className="overflow-hidden">
              <div className="border-b border-rule bg-paper-warm px-5 py-2.5">
                <SectionLabel>You</SectionLabel>
              </div>
              {/* Cards on phones, table from md up — see PersonCards. */}
              <div className="md:hidden">
                <PersonCards rows={[self]} onOpen={(row, tab) => setOpen({ row, tab })} />
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[720px]">
                  {Header}
                  <tbody>
                    <PersonRows rows={[self]} onOpen={(row, tab) => setOpen({ row, tab })} />
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {grouped.map((group) => (
            <Panel key={group.role} padded={false} className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-rule bg-paper-warm px-5 py-2.5">
                <SectionLabel>{group.heading}</SectionLabel>
                <span className="text-[11px] text-ink-ghost">{group.rows.length}</span>
              </div>
              <div className="md:hidden">
                <PersonCards rows={group.rows} onOpen={(row, tab) => setOpen({ row, tab })} />
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[720px]">
                  {Header}
                  <tbody>
                    <PersonRows rows={group.rows} onOpen={(row, tab) => setOpen({ row, tab })} />
                  </tbody>
                </table>
              </div>
            </Panel>
          ))}

          {grouped.length === 0 && !self && (
            <Panel>
              <p className="text-center text-[13px] text-ink-faint">
                Nothing to report for {monthLabel}.
              </p>
            </Panel>
          )}

          <p className="text-center text-[11.5px] leading-relaxed text-ink-ghost">
            Present counts days with a clock-in. Leave counts approved days falling inside this
            month. Flags shown are the ones still open — waived ones are listed separately.
            Tap any flag or task count to see what is behind it.
          </p>
        </div>
      )}

      <PersonMonthDialog
        personId={open?.row.person.id ?? null}
        personName={open?.row.person.name ?? ''}
        tab={open?.tab ?? 'flags'}
        year={year}
        month={month}
        monthLabel={monthLabel}
        onClose={() => {
          setOpen(null);
          if (params.has('person')) {
            params.delete('person');
            setParams(params, { replace: true });
          }
        }}
      />
    </AppShell>
  );
}

export default function ReportsRoute() {
  return (
    <AuthGuard>
      <ReportsPage />
    </AuthGuard>
  );
}
